import { useEffect, useRef, useState } from 'react';

/**
 * Why a choice of files cannot be checked, or `null` when it can: the modal
 * takes exactly one `.json` file. Anything about its content is left to the
 * check itself.
 */
export const motivoScartoScelta = (files) => {
  if (files.length !== 1) {
    return 'Carica un solo file alla volta.';
  }
  const [file] = files;
  if (!file.name.toLowerCase().endsWith('.json')) {
    return `«${file.name}» non è un file .json: carica l'Export JSON di HACCP Agent.`;
  }
  return null;
};

/**
 * The steps of an import from HACCP Agent, from the choice of the file to the
 * write.
 *
 * `verifica(file, { signal })` reads the file and checks it without writing
 * anything. It resolves to `{ riepilogo, contenuto }`: what the confirmation
 * shows, and the text that was checked. It rejects with an `errori` array
 * when the file is not compliant, and with any other error when the check
 * could not run. `applica(contenuto)` writes that same text, never the file
 * read again, so what is written is what the recap described even if the file
 * changed on disk since. It rejects the same two ways: the backend checks the
 * text again before writing it.
 *
 * `passo` is one of `selezione`, `verifica`, `nonConforme`, `erroreTecnico`
 * (with `fase` telling the check from the write, and the error's `status` and
 * `messaggio`), `conferma` and `scrittura`.
 */
const useFlussoImportHaccp = ({ verifica, applica, onImportato }) => {
  const [stato, setStato] = useState({ passo: 'selezione' });
  const controller = useRef(null);
  const montato = useRef(true);

  useEffect(
    () => () => {
      montato.current = false;
      controller.current?.abort();
    },
    [],
  );

  const avviaVerifica = (file) => {
    const ctrl = new AbortController();
    controller.current = ctrl;
    setStato({ passo: 'verifica', file });
    verifica(file, { signal: ctrl.signal })
      .then(({ riepilogo, contenuto }) => {
        if (ctrl.signal.aborted) return;
        setStato({ passo: 'conferma', file, riepilogo, contenuto });
      })
      .catch((error) => {
        if (ctrl.signal.aborted) return;
        setStato(
          Array.isArray(error?.errori)
            ? { passo: 'nonConforme', file, errori: error.errori }
            : {
                passo: 'erroreTecnico',
                fase: 'verifica',
                file,
                status: error?.status,
                messaggio: error?.message,
              },
        );
      });
  };

  const scegliFile = (files) => {
    const motivoScarto = motivoScartoScelta(files);
    if (motivoScarto) {
      setStato({ passo: 'selezione', motivoScarto });
      return;
    }
    avviaVerifica(files[0]);
  };

  const scrivi = ({ file, riepilogo, contenuto }) => {
    setStato({ passo: 'scrittura', file, riepilogo, contenuto });
    applica(contenuto)
      .then(() => {
        if (montato.current) onImportato();
      })
      .catch((error) => {
        if (!montato.current) return;
        setStato(
          Array.isArray(error?.errori)
            ? { passo: 'nonConforme', file, errori: error.errori }
            : {
                passo: 'erroreTecnico',
                fase: 'scrittura',
                file,
                riepilogo,
                contenuto,
                status: error?.status,
                messaggio: error?.message,
              },
        );
      });
  };

  const riprova = () =>
    stato.fase === 'scrittura' ? scrivi(stato) : avviaVerifica(stato.file);

  return {
    ...stato,
    scegliFile,
    conferma: () => scrivi(stato),
    riprova,
    caricaAltroFile: () => setStato({ passo: 'selezione' }),
    // Stops a check in flight. A write is never stopped: the modal cannot be
    // closed while one runs.
    interrompi: () => controller.current?.abort(),
  };
};

export default useFlussoImportHaccp;
